import * as React from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Button, Form, Modal } from "semantic-ui-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SemanticReactHookFormsInput } from "@teamprevent/common/forms/SemanticReactHookFormsInput";
import { SemanticReactHookFormsDropdown } from "@teamprevent/common/forms/SemanticReactHookFormsDropdown";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";

import { CustomerTeamCreateUpdateModel, customerTeamCreateUpdateModelSchemaFactory, CustomerTeam } from "../model";
import { customerApi, selectors as customersSelectors } from "../../customer";

interface Props {
    teams: CustomerTeam[];
    title: string;
    isSavingRequestName: string;
    onSave: (model: CustomerTeamCreateUpdateModel) => Promise<void>;
    onClose: () => void;
    defaultValues: CustomerTeamCreateUpdateModel;
}

export const CreateEditModal: React.FunctionComponent<Props> = ({ teams, title, isSavingRequestName, onSave, defaultValues, onClose }) => {
    const { control, handleSubmit } = useForm<CustomerTeamCreateUpdateModel>({
        resolver: zodResolver(customerTeamCreateUpdateModelSchemaFactory(teams ?? [])),
        defaultValues
    });
    const customerId = useSelector(customersSelectors.routeIdSelector);
    const saveTeam = React.useCallback(async (model: CustomerTeamCreateUpdateModel) => {
        await onSave(model);
        onClose();
    }, [onClose, onSave]);
    const submit = handleSubmit(saveTeam);
    const [customerUsers, setCustomerUsers] = React.useState<{ key: string; text: string; value: string }[] | undefined>(undefined);
    const isMountedRef = React.useRef(true);
    React.useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);
    React.useEffect(() => {
        (async () => {
            const users = await customerApi.customerUsers(customerId);
            // The component may have unloaded while we were waiting for the API call to complete
            if (isMountedRef.current) {
                const usersInOtherTeams = teams.flatMap(team => team.members.map(member => member.id));
                setCustomerUsers(users.map(user => ({
                    key: user.id,
                    value: user.id,
                    text: `${user.firstName} ${user.surname}`,
                    disabled: usersInOtherTeams.includes(user.id)
                })));
            }
        })();
    }, [customerId, teams]);

    return (
        <Modal as={Form} onSubmit={submit} size="small" open onClose={onClose} closeOnDimmerClick={false}>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Content>
                <SemanticReactHookFormsInput name="name" label="Name" control={control} />
                <SemanticReactHookFormsDropdown
                    name="memberIds"
                    label="Members"
                    control={control}
                    options={customerUsers ?? []}
                    multiple
                    search
                    loading={customerUsers === undefined}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={onClose}>Cancel</Button>
                <IsLoadingButton type="submit" requestNames={isSavingRequestName} content="Save" />
            </Modal.Actions>
        </Modal>
    );
};
